import { useEffect, useState } from 'react';
import {
  CheckCircle2,
  Loader2,
  Plus,
  RotateCcw,
  TriangleAlert,
} from 'lucide-react';

import CompactInputField from '../../../../../components/Forms/Input/CompactInputField';
import CompactSelectField from '../../../../../components/Forms/Input/CompactSelectField';
import { DteImportRow } from '../../../../../types/restaurant/RestaurantDteImportTypes';
import { RestaurantRawMaterialDTO } from '../../../../../types/restaurant/RestaurantRawMaterialTypes';
import PurchaseLineConversionHint from '../../PurchaseLineConversionHint';

interface RawMaterialOption {
  id: number;
  name: string;
  code?: string;
}

interface Props {
  row: DteImportRow;
  options: RawMaterialOption[];
  /** True cuando ya hay un proveedor (registrado o encontrado). */
  supplierReady: boolean;
  /** Materia prima vinculada (para la vista previa de conversión). */
  rawMaterial?: RestaurantRawMaterialDTO;
  /** Nombre de la unidad de nuestro catálogo a la que mapea la uniMedida del DTE. */
  dteUnitName?: string;
  /** Nombre de la unidad base de la materia prima. */
  baseUnitName?: string;
  /** True mientras se guarda la equivalencia de unidad de esta línea. */
  savingRule?: boolean;
  onSelectRawMaterial: (rawMaterialId: number) => void;
  onUndoLink: () => void;
  onCreateRawMaterial: () => void;
  onCreateConversionRule?: (factor: number) => void;
  onChange: (patch: Partial<DteImportRow>) => void;
}

const currency = new Intl.NumberFormat('es-SV', {
  style: 'currency',
  currency: 'USD',
});

const fieldLabel =
  'mb-1.5 block text-[11px] font-medium uppercase tracking-wide text-bodydark2';

/**
 * Una línea del DTE dentro del asistente de importación: descripción / código del
 * documento, la materia prima a la que se liga y la cantidad y el costo unitario.
 * Al elegir la materia prima de una línea "sin identificar" se crea al instante
 * el identificador del proveedor (no se espera a "Continuar").
 */
const DteImportLineCard = ({
  row,
  options,
  supplierReady,
  rawMaterial,
  dteUnitName,
  baseUnitName,
  savingRule,
  onSelectRawMaterial,
  onUndoLink,
  onCreateRawMaterial,
  onCreateConversionRule,
  onChange,
}: Props) => {
  const lineTotal = Number(row.quantity || 0) * Number(row.unitCost || 0);
  const editable = !row.matched && !row.linking;

  const [factor, setFactor] = useState('');
  // Al cambiar de estado "sin conversión", limpia el campo.
  useEffect(() => {
    if (!row.needsConversionRule) setFactor('');
  }, [row.needsConversionRule]);

  return (
    <div className="rounded-xl border border-stroke bg-white p-3.5 dark:border-strokedark dark:bg-boxdark">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="text-sm font-semibold text-black dark:text-white">
            {row.line.description || '(sin descripción)'}
          </p>
          <p className="font-mono text-[11px] text-bodydark2">
            {row.line.code || 'sin código'} · {row.line.quantity} u ·{' '}
            {currency.format(row.line.unitPrice)} c/u
          </p>
        </div>
        {row.linking ? (
          <span className="inline-flex shrink-0 items-center gap-1 rounded-full bg-primary/10 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-primary">
            <Loader2 className="h-3 w-3 animate-spin" /> Vinculando
          </span>
        ) : row.matched ? (
          <span className="inline-flex shrink-0 items-center gap-1 rounded-full bg-success/10 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-success">
            <CheckCircle2 className="h-3 w-3" />
            {row.preexisting ? 'Producto reconocido' : 'Identificador creado'}
          </span>
        ) : (
          <span className="inline-flex shrink-0 items-center rounded-full bg-warning/15 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-meta-8 dark:text-warning">
            Sin identificar
          </span>
        )}
      </div>

      <div className="mt-3 grid items-start gap-3 sm:grid-cols-[minmax(0,1fr)_4.75rem_6rem]">
        <div>
          <span className={fieldLabel}>Materia prima</span>
          <CompactSelectField
            name={`rm-${row.line.ref}`}
            value={row.rawMaterialId === '' ? '' : row.rawMaterialId}
            iconKey="product"
            disabled={row.matched || row.linking || !supplierReady}
            placeholder={
              supplierReady
                ? 'Elegir materia prima'
                : 'Registra el proveedor primero'
            }
            options={options.map((option) => ({
              value: option.id,
              label: option.code
                ? `${option.name} · ${option.code}`
                : option.name,
            }))}
            onChange={(event) => {
              const next = Number(event.target.value);
              if (next) onSelectRawMaterial(next);
            }}
          />
        </div>

        <div>
          <span className={fieldLabel}>Cantidad</span>
          <CompactInputField
            name={`qty-${row.line.ref}`}
            type="number"
            min={0}
            step="any"
            value={row.quantity}
            onChange={(event) =>
              onChange({
                quantity:
                  event.target.value === '' ? '' : Number(event.target.value),
              })
            }
          />
        </div>

        <div>
          <span className={fieldLabel}>Costo unit.</span>
          <CompactInputField
            name={`cost-${row.line.ref}`}
            type="number"
            min={0}
            step="any"
            value={row.unitCost}
            onChange={(event) =>
              onChange({
                unitCost:
                  event.target.value === '' ? '' : Number(event.target.value),
              })
            }
          />
        </div>
      </div>

      {/* Vista previa de la conversión a la unidad base (cuando ya hay ruta). */}
      {rawMaterial && !!row.rawMaterialId && !row.needsConversionRule && (
        <PurchaseLineConversionHint
          rawMaterial={rawMaterial}
          fromUnitId={row.unitId}
          quantity={row.quantity}
        />
      )}

      {/* La unidad del DTE no llega a la unidad base: capturar la equivalencia. */}
      {!!row.rawMaterialId && row.needsConversionRule && (
        <div className="mt-2.5 rounded-lg border border-warning/40 bg-warning/5 p-2.5 text-xs">
          <p className="flex items-start gap-1.5 font-medium text-meta-8 dark:text-warning">
            <TriangleAlert className="mt-0.5 h-3.5 w-3.5 shrink-0" />
            El DTE factura en «{dteUnitName || 'esa unidad'}», pero{' '}
            {rawMaterial?.name ?? 'la materia prima'} se maneja en «
            {baseUnitName || 'su unidad base'}» y no hay una equivalencia. Indica
            cuánto equivale:
          </p>
          <div className="mt-2 flex flex-wrap items-center gap-2">
            <span className="text-bodydark2">1 {dteUnitName || 'unidad'} =</span>
            <input
              type="number"
              min={0}
              step="any"
              value={factor}
              onChange={(event) => setFactor(event.target.value)}
              placeholder="0"
              className="w-24 rounded-md border border-stroke bg-white px-2 py-1 text-sm text-black outline-none focus:border-primary dark:border-strokedark dark:bg-boxdark dark:text-white"
            />
            <span className="text-bodydark2">{baseUnitName || 'unidad base'}</span>
            <button
              type="button"
              disabled={savingRule || !(Number(factor) > 0)}
              onClick={() => onCreateConversionRule?.(Number(factor))}
              className="inline-flex items-center gap-1 rounded-md bg-primary px-2.5 py-1 text-[11px] font-semibold text-white transition hover:bg-opacity-90 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {savingRule && <Loader2 className="h-3 w-3 animate-spin" />}
              {savingRule ? 'Guardando…' : 'Guardar equivalencia'}
            </button>
          </div>
          <p className="mt-1.5 text-[10px] leading-4 text-bodydark2">
            Se guarda como conversión de esta materia prima; las próximas compras
            en «{dteUnitName || 'esa unidad'}» ya se convierten solas.
          </p>
        </div>
      )}

      {/* Identificador del proveedor — editable mientras la línea no está identificada */}
      {editable && (
        <div className="mt-2.5 rounded-lg border border-stroke bg-gray-2/40 p-2.5 dark:border-strokedark dark:bg-meta-4/20">
          <span className="mb-1 block text-[10px] font-medium uppercase tracking-wide text-bodydark2">
            Nombre del proveedor para este producto
          </span>
          <CompactInputField
            name={`alias-name-${row.line.ref}`}
            type="text"
            value={row.aliasName}
            placeholder="Sin nombre"
            onChange={(event) => onChange({ aliasName: event.target.value })}
          />
          <p className="mt-1 text-[10px] leading-4 text-bodydark2">
            Al elegir la materia prima se guarda este nombre como identificador
            del proveedor; la próxima importación ya lo reconoce.
          </p>
        </div>
      )}

      <div className="mt-2 flex flex-wrap items-center justify-between gap-x-4 gap-y-1">
        <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
          {editable && (
            <button
              type="button"
              className="inline-flex items-center gap-1 text-[11px] font-medium text-primary hover:underline"
              onClick={onCreateRawMaterial}
            >
              <Plus className="h-3 w-3" /> Crear materia prima
            </button>
          )}
          {row.matched && !row.preexisting && !row.linking && (
            <button
              type="button"
              className="inline-flex items-center gap-1 text-[11px] font-medium text-danger hover:underline"
              onClick={onUndoLink}
            >
              <RotateCcw className="h-3 w-3" /> Deshacer identificador
            </button>
          )}
        </div>
        <p className="text-[11px] text-bodydark2">
          Subtotal línea{' '}
          <span className="font-semibold text-black dark:text-white">
            {currency.format(lineTotal)}
          </span>
        </p>
      </div>
    </div>
  );
};

export default DteImportLineCard;
